import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Home, Monitor, Users, Trees, Truck, Star, Briefcase,
  MapPin, ShieldCheck, Clock3, Star as StarFilled, CreditCard,
  CheckCircle2, ArrowRight,
} from "lucide-react";
import { SERVICE_CONFIG_CATALOG } from "../../config/pricing";
import { ALL_PLANS } from "../membership/membershipPlansData";

// Real Category -> Service -> Option example, pulled live so it can never
// drift out of sync with the actual catalogue (see pricing.js:929-943).
const HOUSE_KEEPING = SERVICE_CONFIG_CATALOG?.["home-services"]?.services?.["house-keeping"];
const SWEEPING_OPTION = HOUSE_KEEPING?.types?.find((t) => t.value === "sweeping");

// Same 7 categories, same routes, same order as Header.js's navItems
// (Header.js:346-354) — kept in sync by hand since navItems isn't exported.
const CATEGORIES = [
  { icon: Home,      label: "Home Services",      path: "/services/instant-home-services" },
  { icon: Monitor,   label: "Technical Services",  path: "/services/technical-services" },
  { icon: Users,     label: "Caretaker Services",  path: "/services/caretaker-services" },
  { icon: Trees,     label: "Outdoor Services",    path: "/services/outdoor-services" },
  { icon: Truck,     label: "Transport Services",  path: "/services/transport-services" },
  { icon: Star,      label: "Special Services",    path: "/services/special-services" },
  { icon: Briefcase, label: "Corporate Services",  path: "/services/corporate-services" },
];

const BOOKING_TYPES = [
  {
    key: "instant",
    icon: "⚡",
    color: "#4f46e5",
    bg: "#eef2ff",
    title: "Instant Service",
    subtitle: "Need it now? Book an available partner near you.",
    steps: ["Select Service", "Confirm Location", "Pay", "Partner Assigned", "Partner Arrives"],
    cta: "Book Instant Service",
    path: "/services/instant-home-services",
  },
  {
    key: "schedule",
    icon: "📅",
    color: "#16a34a",
    bg: "#dcfce7",
    title: "Schedule Service",
    subtitle: "Choose the date and time that works for you.",
    steps: ["Select Service", "Pick Date & Slot", "Pay", "Partner Assigned", "Service Completed"],
    cta: "Schedule a Service",
    path: "/schedule-booking",
  },
  {
    key: "package",
    icon: "📦",
    color: "#ea580c",
    bg: "#ffedd5",
    title: "Package",
    subtitle: "Get more done in a single visit with a Premium tier.",
    steps: ["Choose Service", "Pick Standard or Premium", "Pay", "Partner Assigned"],
    cta: "Explore Packages",
    path: "/services/special-services",
  },
  {
    key: "plan",
    icon: "💎",
    color: "#d97706",
    bg: "#fef3c7",
    title: "Plan",
    subtitle: "Get recurring services for your selected plan period.",
    steps: ["Select Plan", "Choose Period", "Pay", "Recurring Visits"],
    cta: "Explore Plans",
    path: "/subscription-plans",
  },
];

const JOURNEY_STEPS = [
  { no: "01", title: "Choose",    text: "Choose a service from ClickNido's categories." },
  { no: "02", title: "Customize", text: "Select service option, duration, quantity and add-ons." },
  { no: "03", title: "Book",      text: "Choose Instant, Schedule, Package or Plan." },
  { no: "04", title: "Confirm",   text: "Enter/confirm your location and review your booking." },
  { no: "05", title: "Pay",       text: "Complete secure payment." },
  { no: "06", title: "Service",   text: "Your eligible partner arrives and completes the service." },
  { no: "07", title: "Review",    text: "Rate your experience and provide feedback." },
];

const PARTNER_FACTORS = [
  "Service skill", "Location", "Availability", "Partner type",
  "Workload", "Customer rating", "Acceptance performance", "Estimated arrival time",
];

const STATUS_JOURNEYS = [
  {
    key: "instant",
    label: "Instant",
    color: "#4f46e5",
    steps: ["Booking Confirmed", "Finding Partner", "Partner Assigned", "Partner On The Way", "Partner Arrived", "Service Completed"],
  },
  {
    key: "schedule",
    label: "Schedule",
    color: "#16a34a",
    steps: ["Booking Confirmed", "Accepted", "Partner On The Way", "Service Started", "Service Completed"],
    note: "Package bookings (Deep Cleaning's Premium tier, for example) follow this same Schedule journey.",
  },
  {
    key: "plan",
    label: "Plan",
    color: "#d97706",
    steps: ["Plan Activated", "Visit Scheduled", "Partner Assigned", "Visit Completed", "Next Visit / Renewal"],
  },
];

function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

function HowItWorks() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [activeStatus, setActiveStatus] = useState("instant");

  // No existing SEO framework in this app (no react-helmet / document.title
  // usage anywhere in src/) — smallest possible dependency-free way to set
  // a page title + meta description, restored on unmount.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "How ClickNido Works | Book Home & Professional Services";
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    const prevContent = meta.getAttribute("content");
    meta.setAttribute(
      "content",
      "Book trusted home, technical, care and professional services with ClickNido. Choose Instant, Schedule, Package or Plan and get your service arranged easily."
    );
    return () => {
      document.title = prevTitle;
      if (created) meta.remove();
      else if (prevContent !== null) meta.setAttribute("content", prevContent);
    };
  }, []);

  const styles = {
    page: {
      minHeight: "100vh",
      background: "linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%)",
      padding: "40px 16px 64px",
      fontFamily: '"Poppins", "Segoe UI", sans-serif',
    },
    container: { maxWidth: "1180px", margin: "0 auto" },
    hero: { textAlign: "center", marginBottom: "36px" },
    badge: {
      display: "inline-block", padding: "8px 14px", borderRadius: "999px",
      backgroundColor: "#e8f0ff", color: "#0d6efd", fontSize: "13px", fontWeight: 700, marginBottom: "14px",
    },
    h1: { margin: "0 0 12px 0", fontSize: isMobile ? "26px" : "36px", fontWeight: 800, color: "#0f172a", lineHeight: 1.25 },
    subtitle: { margin: "0 auto 22px", maxWidth: "680px", fontSize: "15px", lineHeight: 1.7, color: "#475569" },
    primaryBtn: {
      background: "linear-gradient(90deg, #5d5afe 0%, #6c45ff 100%)", color: "#fff", border: "none",
      borderRadius: "12px", height: "48px", padding: "0 26px", fontSize: "15px", fontWeight: 700,
      boxShadow: "0 12px 24px rgba(103, 88, 255, 0.22)", cursor: "pointer", display: "inline-flex",
      alignItems: "center", gap: "8px",
    },
    section: { marginTop: "56px" },
    sectionHead: { textAlign: "center", marginBottom: "26px" },
    h2: { margin: "0 0 8px 0", fontSize: isMobile ? "22px" : "26px", fontWeight: 800, color: "#0f172a" },
    sectionText: { margin: "0 auto", maxWidth: "640px", fontSize: "14px", color: "#64748b", lineHeight: 1.7 },

    // 7-step journey strip
    journeyRow: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(7, 1fr)",
      gap: "14px",
    },
    journeyCard: {
      backgroundColor: "#ffffff", borderRadius: "18px", padding: "18px 14px",
      boxShadow: "0 10px 28px rgba(15, 23, 42, 0.06)", border: "1px solid rgba(15, 23, 42, 0.06)",
      textAlign: "center",
    },
    journeyNo: { fontSize: "12px", fontWeight: 800, color: "#a5b4fc", marginBottom: "6px" },
    journeyTitle: { fontSize: "15px", fontWeight: 700, color: "#0f172a", margin: "0 0 4px 0" },
    journeyText: { fontSize: "12.5px", color: "#64748b", lineHeight: 1.5, margin: 0 },

    // Booking type cards
    bookingGrid: { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "20px" },
    bookingCard: {
      backgroundColor: "#ffffff", borderRadius: "22px", padding: "26px",
      boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)", border: "1px solid rgba(15, 23, 42, 0.06)",
    },
    bookingIconWrap: (bg) => ({
      width: "52px", height: "52px", borderRadius: "16px", background: bg,
      display: "flex", alignItems: "center", justifyContent: "center", fontSize: "26px", marginBottom: "14px",
    }),
    bookingTitle: { margin: "0 0 6px 0", fontSize: "19px", fontWeight: 800, color: "#0f172a" },
    bookingSubtitle: { margin: "0 0 16px 0", fontSize: "13.5px", color: "#64748b", lineHeight: 1.6 },
    stepChipsRow: { display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "20px" },
    stepChip: (color, bg) => ({
      fontSize: "11.5px", fontWeight: 700, color, backgroundColor: bg,
      padding: "6px 10px", borderRadius: "999px",
    }),
    bookingCta: (color) => ({
      border: "none", background: color, color: "#fff", borderRadius: "12px",
      height: "44px", padding: "0 20px", fontSize: "14px", fontWeight: 700, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: "6px",
    }),

    // Category structure explainer
    chainCard: {
      backgroundColor: "#ffffff", borderRadius: "18px", padding: "20px",
      boxShadow: "0 10px 28px rgba(15, 23, 42, 0.06)", border: "1px solid rgba(15, 23, 42, 0.06)",
      marginBottom: "12px",
    },
    chainRow: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px", fontSize: "13.5px" },
    chainNode: { fontWeight: 700, color: "#0f172a", background: "#f1f5f9", borderRadius: "999px", padding: "6px 12px" },
    chainArrow: { color: "#94a3b8" },

    // Category grid
    categoryGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
      gap: "14px",
    },
    categoryCard: {
      backgroundColor: "#ffffff", borderRadius: "16px", padding: "18px 14px", textAlign: "center",
      boxShadow: "0 10px 28px rgba(15, 23, 42, 0.06)", border: "1px solid rgba(15, 23, 42, 0.06)",
      textDecoration: "none", color: "inherit", display: "block", transition: "transform 0.15s, box-shadow 0.15s",
    },
    categoryIconWrap: {
      width: "44px", height: "44px", borderRadius: "12px", background: "#eef2ff", color: "#4f46e5",
      display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px",
    },
    categoryLabel: { fontSize: "13.5px", fontWeight: 700, color: "#0f172a" },

    // Custom / partner / pricing sections
    infoCard: {
      backgroundColor: "#ffffff", borderRadius: "22px", padding: "28px",
      boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)", border: "1px solid rgba(15, 23, 42, 0.06)",
    },
    factorGrid: {
      display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: "10px", margin: "18px 0",
    },
    factorChip: {
      display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: 600, color: "#334155",
      background: "#f8fafc", borderRadius: "10px", padding: "10px 12px", border: "1px solid #e2e8f0",
    },
    quote: {
      fontSize: "14px", fontStyle: "italic", color: "#0f172a", background: "#eef2ff",
      borderRadius: "14px", padding: "16px 20px", margin: "18px 0 0", lineHeight: 1.6,
    },

    // Status tabs
    statusTabsRow: { display: "flex", gap: "10px", justifyContent: "center", marginBottom: "22px", flexWrap: "wrap" },
    statusTab: (active, color) => ({
      border: `1px solid ${active ? color : "#e2e8f0"}`, background: active ? color : "#fff",
      color: active ? "#fff" : "#334155", borderRadius: "999px", padding: "8px 18px",
      fontSize: "13px", fontWeight: 700, cursor: "pointer",
    }),
    statusStrip: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px", justifyContent: "center" },
    statusStep: (color) => ({
      display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: 700, color,
      background: "#fff", border: `1px solid ${color}33`, borderRadius: "999px", padding: "8px 14px",
    }),
    statusNote: { textAlign: "center", fontSize: "12.5px", color: "#94a3b8", marginTop: "14px" },

    priceList: { margin: "18px 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "10px" },
    priceItem: { display: "flex", alignItems: "center", gap: "10px", fontSize: "14px", color: "#334155" },
    totalRow: {
      display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "18px",
      paddingTop: "16px", borderTop: "2px solid #e2e8f0", fontSize: "16px", fontWeight: 800, color: "#0f172a",
    },

    finalCtaBand: {
      marginTop: "56px", background: "linear-gradient(135deg, #0f172a, #1e3a8a)", borderRadius: "24px",
      padding: isMobile ? "28px 20px" : "40px 48px", textAlign: "center", color: "#fff",
    },
    finalCtaBtnRow: { display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "12px", marginTop: "22px" },
    ghostBtn: (color) => ({
      border: `1px solid ${color}`, background: "rgba(255,255,255,0.06)", color: "#fff",
      borderRadius: "12px", height: "44px", padding: "0 18px", fontSize: "13.5px", fontWeight: 700, cursor: "pointer",
    }),
  };

  const activeJourney = STATUS_JOURNEYS.find((j) => j.key === activeStatus) || STATUS_JOURNEYS[0];

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* Hero */}
        <div style={styles.hero}>
          <span style={styles.badge}>How ClickNido Works</span>
          <h1 style={styles.h1}>
            Choose a Service. Choose How You Want It. We Take Care of the Rest.
          </h1>
          <p style={styles.subtitle}>
            From an urgent repair to a recurring home-care plan, ClickNido gets you a
            verified partner in a few simple steps — no lengthy forms, no guesswork.
          </p>
          <button style={styles.primaryBtn} onClick={() => navigate("/services/all-services")}>
            Book a Service <ArrowRight size={16} />
          </button>
        </div>

        {/* 7-step journey */}
        <section style={styles.section}>
          <div style={styles.sectionHead}>
            <h2 style={styles.h2}>Your Journey in 7 Steps</h2>
            <p style={styles.sectionText}>From choosing a service to leaving a review — here's the whole trip.</p>
          </div>
          <div style={styles.journeyRow}>
            {JOURNEY_STEPS.map((s) => (
              <div key={s.no} style={styles.journeyCard}>
                <div style={styles.journeyNo}>{s.no}</div>
                <h3 style={styles.journeyTitle}>{s.title}</h3>
                <p style={styles.journeyText}>{s.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 4 booking types */}
        <section style={styles.section}>
          <div style={styles.sectionHead}>
            <h2 style={styles.h2}>Four Ways to Get It Done</h2>
            <p style={styles.sectionText}>Every service can be booked the way that suits you best.</p>
          </div>
          <div style={styles.bookingGrid}>
            {BOOKING_TYPES.map((b) => (
              <div key={b.key} style={styles.bookingCard}>
                <div style={styles.bookingIconWrap(b.bg)}>{b.icon}</div>
                <h3 style={styles.bookingTitle}>{b.title}</h3>
                <p style={styles.bookingSubtitle}>{b.subtitle}</p>
                <div style={styles.stepChipsRow}>
                  {b.steps.map((s, i) => (
                    <span key={i} style={styles.stepChip(b.color, b.bg)}>{s}</span>
                  ))}
                </div>
                {b.key === "plan" && (
                  <div style={{ ...styles.stepChipsRow, marginTop: "-10px" }}>
                    {ALL_PLANS.map((p) => (
                      <span key={p.id} style={styles.stepChip("#92400e", "#fef3c7")}>
                        {p.name} · {p.period}
                      </span>
                    ))}
                  </div>
                )}
                <button style={styles.bookingCta(b.color)} onClick={() => navigate(b.path)}>
                  {b.cta} <ArrowRight size={15} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Category -> Service -> Option */}
        <section style={styles.section}>
          <div style={styles.sectionHead}>
            <h2 style={styles.h2}>How ClickNido Services Are Organized</h2>
            <p style={styles.sectionText}>
              Every booking follows the same simple path: Category → Service → Option.
            </p>
          </div>

          {SWEEPING_OPTION && (
            <div style={styles.chainCard}>
              <div style={styles.chainRow}>
                <span style={styles.chainNode}>{CATEGORIES[0].label}</span>
                <ArrowRight size={14} style={styles.chainArrow} />
                <span style={styles.chainNode}>{HOUSE_KEEPING.label}</span>
                <ArrowRight size={14} style={styles.chainArrow} />
                <span style={styles.chainNode}>
                  {SWEEPING_OPTION.label} (₹{SWEEPING_OPTION.price}/{SWEEPING_OPTION.unit}, up to {SWEEPING_OPTION.limit})
                </span>
              </div>
            </div>
          )}
          <div style={styles.chainCard}>
            <div style={styles.chainRow}>
              <span style={styles.chainNode}>Technical Services</span>
              <ArrowRight size={14} style={styles.chainArrow} />
              <span style={styles.chainNode}>Electrical</span>
              <ArrowRight size={14} style={styles.chainArrow} />
              <span style={styles.chainNode}>Light Repair / Installation</span>
            </div>
          </div>
          <div style={styles.chainCard}>
            <div style={styles.chainRow}>
              <span style={styles.chainNode}>Caretaker Services</span>
              <ArrowRight size={14} style={styles.chainArrow} />
              <span style={styles.chainNode}>Elder Care</span>
              <ArrowRight size={14} style={styles.chainArrow} />
              <span style={styles.chainNode}>Selected Care Option &amp; Schedule</span>
            </div>
          </div>
        </section>

        {/* Browse by category */}
        <section style={styles.section}>
          <div style={styles.sectionHead}>
            <h2 style={styles.h2}>Browse by Category</h2>
            <p style={styles.sectionText}>Jump straight into any category to see what's available.</p>
          </div>
          <div style={styles.categoryGrid}>
            {CATEGORIES.map((c) => (
              <Link key={c.label} to={c.path} style={styles.categoryCard}>
                <div style={styles.categoryIconWrap}>
                  <c.icon size={20} />
                </div>
                <div style={styles.categoryLabel}>{c.label}</div>
              </Link>
            ))}
          </div>
        </section>

        {/* Custom requirement */}
        <section style={styles.section}>
          <div style={styles.infoCard}>
            <h2 style={{ ...styles.h2, textAlign: "center" }}>Need a Custom Care Arrangement?</h2>
            <p style={{ ...styles.sectionText, margin: "0 auto 18px" }}>
              Tell us what you need — baby, elder, patient or nursing care — and ClickNido
              will review your requirement and arrange the right partner for you.
            </p>
            <div style={{ textAlign: "center" }}>
              <button style={styles.primaryBtn} onClick={() => navigate("/services/caretaker-services")}>
                Request a Service <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </section>

        {/* Partner assignment */}
        <section style={styles.section}>
          <div style={styles.infoCard}>
            <h2 style={{ ...styles.h2, textAlign: "center" }}>We Find the Right Partner for You</h2>
            <p style={{ ...styles.sectionText, margin: "0 auto" }}>
              After your booking is confirmed, ClickNido checks for an eligible partner based on:
            </p>
            <div style={styles.factorGrid}>
              {PARTNER_FACTORS.map((f) => (
                <div key={f} style={styles.factorChip}>
                  <CheckCircle2 size={15} color="#16a34a" /> {f}
                </div>
              ))}
            </div>
            <p style={styles.quote}>
              "ClickNido finds an eligible partner based on your service, location and availability."
            </p>
          </div>
        </section>

        {/* Status journeys */}
        <section style={styles.section}>
          <div style={styles.sectionHead}>
            <h2 style={styles.h2}>What Happens After You Book</h2>
            <p style={styles.sectionText}>A simple status journey, so you always know where things stand.</p>
          </div>
          <div style={styles.statusTabsRow}>
            {STATUS_JOURNEYS.map((j) => (
              <button
                key={j.key}
                style={styles.statusTab(activeStatus === j.key, j.color)}
                onClick={() => setActiveStatus(j.key)}
              >
                {j.label}
              </button>
            ))}
          </div>
          <div style={styles.statusStrip}>
            {activeJourney.steps.map((s, i) => (
              <React.Fragment key={s}>
                <span style={styles.statusStep(activeJourney.color)}>{s}</span>
                {i < activeJourney.steps.length - 1 && <ArrowRight size={14} color="#cbd5e1" />}
              </React.Fragment>
            ))}
          </div>
          {activeJourney.note && <p style={styles.statusNote}>{activeJourney.note}</p>}
        </section>

        {/* Pricing */}
        <section style={styles.section}>
          <div style={styles.infoCard}>
            <h2 style={{ ...styles.h2, textAlign: "center" }}>Know Your Price Before You Book</h2>
            <p style={{ ...styles.sectionText, margin: "0 auto" }}>
              Your final amount may include:
            </p>
            <ul style={styles.priceList}>
              <li style={styles.priceItem}><CreditCard size={15} color="#4f46e5" /> Service price</li>
              <li style={styles.priceItem}><Clock3 size={15} color="#4f46e5" /> Duration / quantity adjustment</li>
              <li style={styles.priceItem}><StarFilled size={15} color="#4f46e5" /> Selected add-ons</li>
              <li style={styles.priceItem}><ShieldCheck size={15} color="#4f46e5" /> Applicable platform fee</li>
              <li style={styles.priceItem}><MapPin size={15} color="#4f46e5" /> Applicable GST / taxes</li>
            </ul>
            <div style={styles.totalRow}>
              <span>Total Payable</span>
              <span>Calculated at checkout</span>
            </div>
          </div>
        </section>

        {/* Final CTA band */}
        <div style={styles.finalCtaBand}>
          <h2 style={{ margin: 0, fontSize: isMobile ? "20px" : "24px", fontWeight: 800 }}>
            Choose → Customize → Book → Pay → Partner Assigned → Service Completed → Review
          </h2>
          <div style={styles.finalCtaBtnRow}>
            {BOOKING_TYPES.map((b) => (
              <button key={b.key} style={styles.ghostBtn(b.color)} onClick={() => navigate(b.path)}>
                {b.cta}
              </button>
            ))}
            <button style={styles.ghostBtn("#fff")} onClick={() => navigate("/services/all-services")}>
              Book a Service
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

export default HowItWorks;
